import { cloneElement, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { UserIcon } from '@panwds/icons'
import {
  Card,
  CardBody,
  CardHeader,
  List,
  ListItem,
  useOnOutsideClick,
} from '@panwds/react-ui'
import { logout } from '../../framework/actions'
import { getPendo } from '../../framework/externals'
import { stateRef } from '../../framework/states'
import { classNames, isNonemptyArray, setTimeout, clearTimeout } from '../../framework/utils'
import { _T } from '../../i18n'
import { FavoriteList } from './FavoriteAction'
import { Feedback, FeedbackMenu } from './feedback'
import { ffUseNav3 } from '../../framework/extensions/utils'
import TsgSelector from './gcsTsgSelector'
import './builtInActions.scss'
import type { VFC, MouseEventHandler, MouseEvent, ReactElement, ReactNode } from 'react'
import type { AuthState, FrameworkVars, OidcState, Preferences, StoreState } from '../../framework/stateTypes'

function useShouldRender(isOpen: boolean): boolean {
  const [shouldRenderChildren, setShouldRenderChildren] = useState<boolean>(isOpen)

  useEffect(() => {
    let timeout: number

    if (isOpen) {
      setShouldRenderChildren(true)
    }
    else {
      timeout = setTimeout(() => {
        setShouldRenderChildren(false)
      }, 200)
    }
    return () => {
      clearTimeout(timeout)
    }
  }, [isOpen])

  return isOpen || shouldRenderChildren
}

const UserDropdownComp: VFC<{
  isSideNavCollapsed: boolean
  title?: ReactNode
  logout?: () => void,
  fxVars?: FrameworkVars
  fallback: ReactElement
}> = ({ isSideNavCollapsed, title, fallback, logout, fxVars }) => {
  const [isOpen, setDropdownState] = useState(false)
  const isOpenRef = useRef(isOpen)
  const menuRef = useRef(null)
  const logoutRef = useRef(logout)
  const shouldRender = useShouldRender(isOpen)
  const opacity0 = !shouldRender || !isOpen
  const gcsTsgSupport = fxVars?.private_site_default_tsg
  const navStyleUpdate = ffUseNav3()

  useOnOutsideClick(menuRef, () => {
    if (isOpenRef.current) {
      setTimeout(() => {
        isOpenRef.current = false
        setDropdownState(false)
      }, 100)
    }
  })

  const handleDropdownToggle = useCallback((e: MouseEvent<HTMLDivElement, MouseEvent>) => {
    const open = !isOpenRef.current
    if (open) {
      isOpenRef.current = open
      setDropdownState(open)
    }
    else {
      e?.stopPropagation()
    }
  }, [])

  logoutRef.current = logout
  const toggle = useMemo(() => {
    const classes = navStyleUpdate ? 'twds-flex twds-box-border twds-m-0 twds-items-center twds-relative twds-pl-2 twds-pr-2 twds-py-2 twds-cursor-pointer hover:twsf-bg-[#404A52] hover:twsf-text-[#E4E8EB] twds-text-sm twds-h-8 twds-w-52 twsf-bg-[#303940] twsf-text-[#A6ADB2] twds-border-transparent twds-rounded' :
      'twds-flex twds-w-full twds-box-border twds-m-0 twds-items-center twds-relative twds-pl-2 twds-pr-2 twds-py-2 twds-cursor-pointer hover:twds-bg-blue-steel-800 dark:hover:twds-bg-gray-800 hover:twds-text-white dark:hover:twds-text-white twds-text-blue-steel-300 dark:twds-text-gray-400 twds-text-sm twds-h-8 twds-ml-2 twds-border-solid twds-border-b-0 twds-border-t-0 twds-border-r-0 twds-border-l-2 twds-border-l-transparent dark:twds-border-l-transparent twds-border-blue-steel-800 dark:twds-border-gray-950 twds-bg-blue-steel-1000 dark:twds-bg-gray-1025 twds-border-t-0 twds-border-transparent'
    const props = isSideNavCollapsed ? {
      onClick: gcsTsgSupport ? handleDropdownToggle : undefined,
      title: navStyleUpdate ? title : <div className={'sparky-user-flayout-title-fix'}>{title}</div>,
      children: isOpen && gcsTsgSupport ? undefined : <button
        id='sparky-user-logout'
        data-metrics='nav-sparky-user-logout'
        data-testid='sparky-user-logout'
        className={classes}
        onClick={logoutRef.current as MouseEventHandler<any>}
      >
        <span className='twds-truncate'>
          {_T('Sign Out')}
        </span>
      </button>
    } : { onClick: handleDropdownToggle }
    return cloneElement(fallback, props)
  }, [isSideNavCollapsed, fallback, title, gcsTsgSupport, handleDropdownToggle, navStyleUpdate, isOpen])

  if (!title && !logout) {
    return null
  }

  const cls = classNames(
    'sparky-oneapp-user-card twsf-rounded twsf-bg-blue-steel-900 dark:twsf-bg-black twsf-color-white dark:twsf-color-white',
    opacity0 && 'twsf-opacity-0',
    isSideNavCollapsed ? '-collapsed' : '-expanded',
  )
  const menu = shouldRender && <Card
    addClassName={cls}
    dataTestId='sparky-user-card'
  >
    <div ref={menuRef} className={gcsTsgSupport ? 'twsf-w-60' : ''}>
      <CardHeader title={title} addClassName='twsf-h-14 twsf-bg-blue-steel-900 dark:twsf-bg-black twsf-color-white' />
      <CardBody addClassName='sparky-user-menu-body twsf-bg-blue-steel-900 dark:twsf-bg-black twsf-color-white twsf-p-0'>
        {gcsTsgSupport && <TsgSelector />}
        <List
          dataTestId='sparky-user-logout'
          onClick={logout as MouseEventHandler<any>}
          style={{ border: 'none', boxShadow: 'none' }}
        >
          <ListItem
            addClassName='sparky-user-logout twsf-bg-blue-steel-900 hover:twsf-bg-blue-steel-800 active:twsf-bg-blue-steel-700 twsf-text-white twsf-text-left'
          >{_T('Sign Out')}</ListItem>
        </List>
      </CardBody>
    </div>
  </Card>

  return <>
    {toggle}
    {menu}
  </>
}

export const UserDropdown = memo(UserDropdownComp)

const getUserName = (oidc: OidcState | undefined = stateRef.current.oidc) => {
  const { oidcUser = oidc } = oidc as any
  if (!oidcUser?.profile) {
    return ''
  }
  const {
    profile: {
      given_name: firstname = '',
      family_name: lastname = '',
      email = ''
    }
  } = oidcUser
  const userName = `${firstname} ${lastname}`.trim() || email
  return userName
}

const getUserDisplay = (oidc: OidcState | undefined = stateRef.current.oidc, auth: AuthState | undefined = stateRef.current.auth, fxVars: FrameworkVars): ReactNode => {
  const userName = getUserName(oidc)
  if (!userName && fxVars.use_provided_tsg_token) {
    return auth.tsg_name || auth.tsg_id
  }
  if (fxVars.private_site_default_tsg && auth?.tsg_id) { // gcs case
    const tsgName = auth.tsg_name || auth.tsg_id
    if (tsgName) {
      return <>
        <div className='twsf-text-left twsf-text-nowrap twsf-text-ellipsis twsf-overflow-x-hidden'>{userName}</div>
        <div className='twsf-text-left twsf-text-xs twsf-leading-tight twsf-text-nowrap twsf-text-ellipsis twsf-overflow-x-hidden' title={auth.tsg_id}>{tsgName}</div>
      </>
    }
  }
  return userName
}

interface BuiltInActionsProps {
  hasPendo?: boolean
  isPrivateSite: boolean
  isSideNavCollapsed: boolean
  oidc: OidcState | undefined
  auth: AuthState | undefined
  preferences: Preferences | undefined
  fxVars: FrameworkVars
}
const builtInActions = ({ hasPendo = Boolean(getPendo()), isPrivateSite, oidc, auth, preferences, fxVars }: BuiltInActionsProps) => [
  fxVars?.use_user_preferences && {
    key: 'favorites',
    navComponent: FavoriteList,
    mapStateToProps: ({ leftMainNavigation }: StoreState) => {
      return {
        hidden: !isNonemptyArray(leftMainNavigation),
        preferences,
        fxVars
      }
    },
  },
  fxVars.feedback && hasPendo && !isPrivateSite && { // hide it for nov release
    key: 'feedback',
    bypassTsgAvail: true,
    bypassGlobalAvail: true,
    _action_early_init: true,
    mapStateToProps: () => {
      return {
        fxVars
      }
    },
    navComponent: fxVars.feedback === 'menu' ? FeedbackMenu : Feedback
  },
  oidc && {
    key: 'user',
    bypassTsgAvail: true,
    bypassGlobalAvail: true,
    _action_early_init: true,
    navComponent: UserDropdown,
    title: () => getUserDisplay(oidc, auth, fxVars),
    icon: UserIcon,
    mapStateToProps: () => {
      return {
        title: getUserDisplay(oidc, auth, fxVars),
        fxVars,
        logout,
      }
    },
  },
].filter(Boolean)

export default builtInActions
